import DomainSelect from 'volta/components/domain-select';
import JsonApiInfinityModel, { decorateApiQuery } from 'volta/models/jsonapi-infinity-model';
import Tag, { TagResourceName } from 'volta/models/tag';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { anyFilter, inFilter, lkFilter, niFilter } from 'volta/utils/filters-utils';

import type { TError } from 'volta/utils/error-utils';
import type { IJsonApiQuery } from 'volta/utils/api/jsonapi-types';
import type { TEntityType } from 'volta/models/types/entity-types';

interface TagSelectArgs {
  entityTypes?: TEntityType[];
  warehouseIds?: string[];
}

export default class TagSelect extends DomainSelect<Tag, TagSelectArgs> {
  get infiniteScroll() {
    return true;
  }
  get idKey() {
    return 'id';
  }
  get placeholder() {
    return this.args.placeholder ?? this.intl.t('addATag');
  }

  /**
   * Here we override the `searchPromise` function to enabled infinite loading
   * and custom filters for entityTypes and warehouseIds.
   * Selected ids filter is also made JSONAPI compatible
   *
   * @param {{search: string, selected: string}} payload Initial payload built by DomainSelect
   * @returns {InfinityModel}
   */
  async searchPromise(payload: Record<string, any>) {
    const { search, excluded } = payload;

    const query: IJsonApiQuery = {
      sort: 'tag'
    };
    const filter: IJsonApiQuery['filter'] = {};

    if (search?.length) {
      filter['tag'] = lkFilter(search);
    }
    if (excluded && excluded.length) {
      // This case should happen only when selected is empty
      // This case should happen only when selected is empty
      filter['excludedIds'] = niFilter(excluded);
    }
    if (this.args.entityTypes?.length) {
      filter['entityTypes'] = anyFilter(this.args.entityTypes);
    }

    if (this.args.warehouseIds?.length) {
      filter['warehouseIds'] = anyFilter(this.args.warehouseIds);
    }
    if (this.values?.length) {
      filter['selectedIds'] = inFilter(this.values);
    }
    query.filter = filter;

    const decoratedQuery = this.args.beforeQuery?.(query) ?? query;

    try {
      return await this.infinity.model(
        TagResourceName.singular,
        decorateApiQuery(decoratedQuery ?? query),
        JsonApiInfinityModel
      );
    } catch (error) {
      defaultErrorHandler(error as TError);
      return [];
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    TagSelect: typeof TagSelect;
    'tag-select': typeof TagSelect;
  }
}
